import { createApi } from '@reduxjs/toolkit/query/react';
import moment from 'moment/moment';
import baseQueryWithTokenManagement from 'pages/auth/apiSlice';

export const supplierApi = createApi({
  tagTypes: [
    'SUPPLIER',
    'ONLINE_SHOP',
    'SUPPLIER_TAGS',
    'SUPPLIER_LIST',
    'COMPETITOR_LIST',
    'SUPPLIER_PRICE_LIST',
    'SUPPLIER_MANAGEMENT',
    'SUPPLIER_SHIPPING_RULES',
    'SUPPLIER_SELECTED_BRANDS',
    'SUPPLIER_SPECIAL_PRICING_RULES',
  ],
  reducerPath: 'supplierApi',
  baseQuery: baseQueryWithTokenManagement,
  endpoints: (builder) => ({
    supplierCreate: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/supplier',
        method: 'post',
        body: data,
      }),
      invalidatesTags: () => ['SUPPLIER_LIST'],
    }),
    supplier: builder.query<any, any>({
      query: (args) => {
        const { size = 10, page = 1, sortField, sortOrder, search, filters } = args;
        const number = page - 1;
        let sortParam = '';
        if (sortField && sortOrder) {
          sortParam = `&sort=${sortField},${sortOrder}`;
        }
        const params = { ...filters, ...(typeof search !== 'undefined' && { search }) };
        return {
          url: `/wrd/api/supplier/list?size=${size}&page=${number}${sortParam}`,
          params: Object.keys(params).length ? params : undefined,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        response.data.content = (response?.data?.content || []).map((item: any) => ({
          ...item,
          formattedLastModifiedDate: {
            full: moment(item.lastModifiedDate).format('DD.MM.YYYY HH:mm:ss'),
            day: moment(item.lastModifiedDate).format('DD.MM.YYYY'),
          },
          formattedCreatedDate: {
            full: moment(item.createDate).format('DD.MM.YYYY HH:mm:ss'),
            day: moment(item.createDate).format('DD.MM.YYYY'),
          },
          key: item.id,
          label: item.name,
          value: item.id,
        }));
        return response.data;
      },
      providesTags: () => ['SUPPLIER_LIST'],
    }),
    supplierSelectList: builder.query<any, void>({
      query: () => {
        return {
          url: `/wrd/api/supplier/selector-list`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        const { mainSupplierId, priceReferenceSupplierId } = response?.data || {};
        response.data = (response?.data?.suppliers || []).map((item: any) => {
          if (item.id === mainSupplierId) {
            item.status = 'MAIN_SUPPLIER';
          } else if (item.id === priceReferenceSupplierId) {
            item.status = 'PRICE_REFERENCE';
          }
          return {
            ...item,
            label: item.name,
            value: item.id,
          };
        });
        return response.data;
      },
      providesTags: () => ['SUPPLIER_LIST'],
    }),
    supplierSelectListForBrand: builder.query<any, any>({
      query: (args = {}) => {
        const { brandId } = args;
        return {
          url: `/wrd/api/supplier/selector-list${brandId ? '?brandId=' + brandId : ''}`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        const { mainSupplierId, priceReferenceSupplierId } = response?.data || {};
        response.data = (response?.data?.suppliers || []).map((item: any) => {
          if (item.id === mainSupplierId) {
            item.status = 'MAIN_SUPPLIER';
          } else if (item.id === priceReferenceSupplierId) {
            item.status = 'PRICE_REFERENCE';
          }
          return {
            ...item,
            label: item.name,
            value: item.id,
          };
        });
        return response.data;
      },
      providesTags: () => ['SUPPLIER_LIST'],
    }),
    supplierSelectListForMultipleBrands: builder.query<any, any>({
      query: (args = {}) => {
        const { brandIds } = args;
        return {
          url: `/wrd/api/supplier/selector-list-multiple${brandIds ? '?brandIds=' + brandIds : ''}`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        const { mainSupplierId, priceReferenceSupplierId } = response?.data || {};
        response.data = (response?.data?.suppliers || []).map((item: any) => {
          if (item.id === mainSupplierId) {
            item.status = 'MAIN_SUPPLIER';
          } else if (item.id === priceReferenceSupplierId) {
            item.status = 'PRICE_REFERENCE';
          }
          return {
            ...item,
            label: item.name,
            value: item.id,
          };
        });
        return response.data;
      },
      providesTags: () => ['SUPPLIER_LIST'],
    }),
    supplierTags: builder.query<any, void>({
      query: () => ({
        url: '/wrd/api/supplier/get-tags',
      }),
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response?.data || [];
      },
      providesTags: () => ['SUPPLIER_TAGS'],
    }),
    supplierById: builder.query<any, { id: string }>({
      query: (args) => {
        const { id } = args;
        return {
          url: `/wrd/api/supplier/${id}`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return {
          ...response.data,
          formattedLastModifiedDate: moment(response.data.lastModifiedDate).format('DD.MM.YYYY HH:mm:ss'),
          formattedCreatedDate: moment(response.data.createDate).format('DD.MM.YYYY HH:mm:ss'),
        };
      },
      providesTags: () => ['SUPPLIER'],
    }),
    supplierDetailsUpdate: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/supplier',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['SUPPLIER', 'SUPPLIER_LIST'],
    }),
    competitorsList: builder.query<any, { id: string }>({
      query: (args) => {
        const { id } = args;
        return {
          url: `/wrd/api/supplier/competitors?id=${id}`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response.data;
      },
      providesTags: () => ['COMPETITOR_LIST', 'SUPPLIER'],
    }),
    supplierTypesList: builder.query<any, void>({
      query: () => {
        return {
          url: `/wrd/api/supplier/types`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        response.data = (response?.data || []).map((item: any) => ({
          label: item,
          value: item,
        }));
        return response.data;
      },
      // providesTags: () => ['COMPETITOR_LIST'],
    }),
    // Price List
    supplierPriceList: builder.query<any, any>({
      query: (args) => {
        const { supplierId, size = 10, page = 1, sortField, sortOrder, search, filters } = args;
        const number = page - 1;
        let sortParam = '';
        if (sortField && sortOrder) {
          sortParam = `&sort=${sortField},${sortOrder}`;
        }
        const params = { ...filters, ...(typeof search !== 'undefined' && { search }) };
        return {
          url: `/wrd/api/price-list/${supplierId}/list?size=${size}&page=${number}${sortParam}`,
          params: Object.keys(params).length ? params : undefined,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        response.data.content = response.data?.content?.map((item: any) => ({
          ...item,
          key: item.id,
        }));
        return response?.data;
      },
      providesTags: () => ['SUPPLIER_PRICE_LIST'],
    }),
    supplierPriceListCreate: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/price-list`,
          method: 'post',
          body,
        };
      },
      invalidatesTags: () => ['SUPPLIER_PRICE_LIST', 'SUPPLIER', 'SUPPLIER_SELECTED_BRANDS', 'SUPPLIER_LIST'],
    }),
    supplierPriceListDelete: builder.mutation<any, { priceListId: string }>({
      query: ({ priceListId }) => {
        return {
          url: `/wrd/api/price-list?priceListId=${priceListId}`,
          method: 'delete',
        };
      },
      invalidatesTags: () => ['SUPPLIER_PRICE_LIST', 'SUPPLIER', 'SUPPLIER_SELECTED_BRANDS', 'SUPPLIER_LIST'],
    }),
    // Online Shop
    supplierOnlineShopCreate: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/supplier/online-shop`,
          method: 'post',
          body,
        };
      },
      invalidatesTags: () => ['ONLINE_SHOP', 'SUPPLIER'],
    }),
    supplierOnlineShopList: builder.query<any, { id: string }>({
      query: (args) => {
        const { id } = args;
        return {
          url: `/wrd/api/supplier/online-shop?supplierId=${id}`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response.data;
      },
      providesTags: () => ['ONLINE_SHOP'],
    }),
    supplierOnlineShopUpdate: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/supplier/online-shop`,
          method: 'put',
          body,
        };
      },
      invalidatesTags: () => ['ONLINE_SHOP', 'SUPPLIER'],
    }),
    supplierOnlineShopDelete: builder.mutation<any, { onlineShopId: string }>({
      query: ({ onlineShopId }) => {
        return {
          url: `/wrd/api/supplier/online-shop?onlineShopId=${onlineShopId}`,
          method: 'delete',
        };
      },
      invalidatesTags: () => ['ONLINE_SHOP', 'SUPPLIER'],
    }),
    selectedBrandsBySupplierId: builder.query<any, any>({
      query: (args) => {
        const { supplierId } = args;
        return {
          url: `/wrd/api/brand/selected-brands?supplierId=${supplierId}`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        response.data = (response?.data || []).map((item: any) => ({
          ...item,
          label: item.name,
          value: item.id,
        }));
        return response.data;
      },
      providesTags: () => ['SUPPLIER_SELECTED_BRANDS'],
    }),
    // Management
    supplierManagementUpdate: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/supplier/management`,
          method: 'put',
          body,
        };
      },
      invalidatesTags: () => ['SUPPLIER', 'SUPPLIER_MANAGEMENT'],
    }),
    supplierManagementRead: builder.query<any, { supplierId: string }>({
      query: ({ supplierId }) => {
        return {
          url: `/wrd/api/supplier/management/${supplierId}?supplierId=${supplierId}`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response.data;
      },
      providesTags: () => ['SUPPLIER_MANAGEMENT'],
    }),

    // Pricing
    supplierPricingRulesUpdate: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/supplier/pricing-rules`,
          method: 'put',
          body,
        };
      },
      invalidatesTags: () => ['SUPPLIER'],
    }),
    supplierLegalUpdate: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/supplier/legal`,
          method: 'put',
          body,
        };
      },
      invalidatesTags: () => ['SUPPLIER'],
    }),
    supplierBankInformationUpdate: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/supplier/bank-information`,
          method: 'put',
          body,
        };
      },
      invalidatesTags: () => ['SUPPLIER'],
    }),

    supplierSpecialPricingRulesCreate: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/supplier/special-pricing-rules`,
          method: 'post',
          body,
        };
      },
      invalidatesTags: () => ['SUPPLIER_SPECIAL_PRICING_RULES', 'SUPPLIER'],
    }),
    supplierSpecialPricingRulesRead: builder.query<any, { supplierId: string }>({
      query: ({ supplierId }) => {
        return {
          url: `/wrd/api/supplier/special-pricing-rules?supplierId=${supplierId}`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response.data;
      },
      providesTags: () => ['SUPPLIER_SPECIAL_PRICING_RULES'],
    }),
    supplierSpecialPricingRulesUpdate: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/supplier/special-pricing-rules`,
          method: 'put',
          body,
        };
      },
      invalidatesTags: () => ['SUPPLIER_SPECIAL_PRICING_RULES', 'SUPPLIER'],
    }),
    supplierSpecialPricingRulesDelete: builder.mutation<any, any>({
      query: ({ specialPricingRuleId }) => {
        return {
          url: `/wrd/api/supplier/special-pricing-rules?specialPricingRuleId=${specialPricingRuleId}`,
          method: 'delete',
        };
      },
      invalidatesTags: () => ['SUPPLIER_SPECIAL_PRICING_RULES', 'SUPPLIER'],
    }),
    supplierSpecialPricingRulesBrand: builder.query<any, { supplierId: string }>({
      query: ({ supplierId }) => {
        return {
          url: `/wrd/api/supplier/special-pricing-rules/brand/${supplierId}`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        response.data = (response?.data || []).map((item: any) => ({
          ...item,
          label: item.name,
          value: item.id,
        }));
        return response.data;
      },
      providesTags: () => ['SUPPLIER_SPECIAL_PRICING_RULES'],
    }),
    // Shipping
    supplierShippingRatesCreate: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/supplier/shipping-rate`,
          method: 'post',
          body,
        };
      },
      invalidatesTags: () => ['SUPPLIER_SHIPPING_RULES', 'SUPPLIER'],
    }),
    supplierShippingRatesRead: builder.query<any, { supplierId: string }>({
      query: ({ supplierId }) => {
        return {
          url: `/wrd/api/supplier/shipping-rate?supplierId=${supplierId}`,
        };
      },
      transformResponse: (response: any) => {
        return response.data;
      },
      providesTags: () => ['SUPPLIER_SHIPPING_RULES'],
    }),
    supplierShippingRatesUpdate: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/supplier/shipping-rate`,
          method: 'put',
          body,
        };
      },
      invalidatesTags: () => ['SUPPLIER_SHIPPING_RULES', 'SUPPLIER'],
    }),
    supplierShippingRatesDelete: builder.mutation<any, { id: string }>({
      query: ({ id }) => {
        return {
          url: `/wrd/api/supplier/shipping-rate?id=${id}`,
          method: 'delete',
        };
      },
      invalidatesTags: () => ['SUPPLIER_SHIPPING_RULES', 'SUPPLIER'],
    }),
    supplierShippingLatestStatistics: builder.query<any, { supplierId: string }>({
      query: ({ supplierId }) => {
        return {
          url: `/wrd/api/supplier/shipping/latest-statistics/${supplierId}`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response.data || {};
      },
      // providesTags: () => ['SUPPLIER_SPECIAL_PRICING_RULES'],
    }),
    supplierFormationType: builder.query<any, void>({
      query: () => {
        return {
          url: `/wrd/api/supplier/formation-type`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response.data || {};
      },
    }),
  }),
});

export const {
  useSupplierQuery,
  useSupplierCreateMutation,
  useSupplierSelectListQuery,
  useSupplierSelectListForBrandQuery,
  useSupplierTagsQuery,
  useSupplierTypesListQuery,
  useCompetitorsListQuery,
  useSupplierByIdQuery,
  useSupplierDetailsUpdateMutation,
  useSupplierPriceListCreateMutation,
  useSupplierPriceListDeleteMutation,
  useSupplierPriceListQuery,
  useSupplierOnlineShopCreateMutation,
  useSupplierOnlineShopListQuery,
  useSupplierOnlineShopUpdateMutation,
  useSupplierOnlineShopDeleteMutation,
  useSelectedBrandsBySupplierIdQuery,
  useSupplierManagementUpdateMutation,
  useSupplierManagementReadQuery,
  useSupplierSpecialPricingRulesCreateMutation,
  useSupplierSpecialPricingRulesUpdateMutation,
  useSupplierSpecialPricingRulesDeleteMutation,
  useSupplierSpecialPricingRulesReadQuery,
  useSupplierSpecialPricingRulesBrandQuery,
  useSupplierLegalUpdateMutation,
  useSupplierBankInformationUpdateMutation,
  useSupplierPricingRulesUpdateMutation,
  useSupplierShippingLatestStatisticsQuery,
  useSupplierShippingRatesCreateMutation,
  useSupplierShippingRatesReadQuery,
  useSupplierShippingRatesUpdateMutation,
  useSupplierShippingRatesDeleteMutation,
  useSupplierFormationTypeQuery,
  useSupplierSelectListForMultipleBrandsQuery,
} = supplierApi;
